import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, MapPinned } from "lucide-react";
import type { Account } from "@shared/schema";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import {
  DELIVERY_ZONES_PATH,
  ZONE_PICK_MATCH,
  ZONE_PICK_NONE,
  addressSourceLabel,
  daysLabel,
  formatAddress,
  zonePickBody,
  zonesByFirstDay,
  type AccountDeliveryDays,
  type DeliveryZonesResponse,
} from "@/lib/deliveryZones";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// The account page's address and delivery days (BRIEF-customer-types-zones-picksheet.md
// §2d, §5 rows 4, 12, 13). The address is a fifth click-to-edit item in the Overview
// tab's Account Info card; the delivery days card renders only in an org that has zones.

type AccountWithAddress = Pick<
  Account,
  | "id"
  | "name"
  | "isGroupParent"
  | "quickbooksCustomerId"
  | "addressLine1"
  | "addressLine2"
  | "city"
  | "province"
  | "postalCode"
  | "addressSource"
  | "deliveryZoneId"
  | "deliveryZoneSource"
> & { addressRefreshedAt: string | Date | null };

type AddressFields = { addressLine1: string; addressLine2: string; city: string; province: string; postalCode: string };

const FIELDS: Array<{ key: keyof AddressFields; label: string; placeholder: string; autoComplete: string }> = [
  { key: "addressLine1", label: "Street address", placeholder: "412 10th St", autoComplete: "address-line1" },
  { key: "addressLine2", label: "Unit, building (optional)", placeholder: "Unit 2", autoComplete: "address-line2" },
  { key: "city", label: "Town or city", placeholder: "Hanover", autoComplete: "address-level2" },
  { key: "province", label: "Province", placeholder: "ON", autoComplete: "address-level1" },
  { key: "postalCode", label: "Postal code", placeholder: "N4N 1A1", autoComplete: "postal-code" },
];

function fieldsOf(account: AccountWithAddress): AddressFields {
  return {
    addressLine1: account.addressLine1 ?? "",
    addressLine2: account.addressLine2 ?? "",
    city: account.city ?? "",
    province: account.province ?? "",
    postalCode: account.postalCode ?? "",
  };
}

function useInvalidateAccount() {
  const queryClient = useQueryClient();
  return () => {
    // Prefix match: the account list and this account's own ["/api/accounts", id] key.
    queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    queryClient.invalidateQueries({ queryKey: [getApiPath(DELIVERY_ZONES_PATH)] });
  };
}

/** Overview → Account Info: the address, with where it came from, click to edit. */
export function AccountAddressItem({ account }: { account: AccountWithAddress }) {
  const { toast } = useToast();
  const invalidate = useInvalidateAccount();
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<AddressFields>(fieldsOf(account));

  const onError = (title: string) => (error: unknown) =>
    toast({ title, description: parseApiError(error).message ?? "Please try again.", variant: "destructive" });

  const save = useMutation({
    mutationFn: (value: AddressFields) => apiRequest("PUT", `/api/accounts/${account.id}/address`, value),
    onSuccess: () => {
      invalidate();
      setOpen(false);
      toast({
        title: "Address saved",
        description: account.quickbooksCustomerId
          ? "Marked as entered by hand, so a QuickBooks refresh won't change it."
          : "Saved.",
      });
    },
    onError: onError("Couldn't save the address"),
  });

  const revertToQuickBooks = useMutation({
    mutationFn: () => apiRequest("POST", `/api/accounts/${account.id}/address/use-quickbooks`),
    onSuccess: () => {
      invalidate();
      setOpen(false);
      toast({ title: "Using the QuickBooks address", description: "It updates whenever addresses are refreshed from QuickBooks." });
    },
    onError: onError("Couldn't use the QuickBooks address"),
  });

  const formatted = formatAddress(account);
  const source = addressSourceLabel(account);
  const busy = save.isPending || revertToQuickBooks.isPending;

  return (
    <div className="flex items-center gap-3">
      <MapPin className="h-4 w-4 text-gray-500 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm text-gray-600">Address</p>
        <button
          className="font-medium text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left break-words"
          data-testid="button-edit-address"
          onClick={() => {
            setFields(fieldsOf(account));
            setOpen(true);
          }}
        >
          {formatted ?? "Not set"}
        </button>
        {source && <p className="text-xs text-gray-500" data-testid="text-address-source">{source}</p>}
      </div>

      <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Address</DialogTitle>
            <DialogDescription>
              Where {account.name} takes deliveries. It prints on the pick sheet and decides their delivery zones.
              {account.quickbooksCustomerId && account.addressSource !== "manual" &&
                " Saving an edit here stops QuickBooks refreshes from changing it."}
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(fields);
            }}
          >
            {FIELDS.map((field) => (
              <div key={field.key} className="space-y-1">
                <Label htmlFor={`address-${field.key}`}>{field.label}</Label>
                <Input
                  id={`address-${field.key}`}
                  data-testid={`input-address-${field.key}`}
                  value={fields[field.key]}
                  maxLength={200}
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                  onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })}
                />
              </div>
            ))}
            <DialogFooter className="gap-2 sm:justify-between">
              {account.quickbooksCustomerId ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => revertToQuickBooks.mutate()}
                  data-testid="button-use-quickbooks-address"
                >
                  {revertToQuickBooks.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Use QuickBooks address
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy} data-testid="button-save-address" className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover">
                  {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Overview tab, beside Fulfillment Access: the delivery days this customer can choose and
 * the zones they come from — the place to look when a customer disputes their days.
 */
export function AccountDeliveryZoneCard({ account }: { account: AccountWithAddress }) {
  const { toast } = useToast();
  const invalidate = useInvalidateAccount();
  // The zones only: under the list's key (so every zone change refreshes it), without the Unzoned list.
  const { data } = useQuery<{ zones: DeliveryZonesResponse["zones"] }>({
    queryKey: [getApiPath(DELIVERY_ZONES_PATH), "zones-only"],
    queryFn: async () => (await apiRequest("GET", `${DELIVERY_ZONES_PATH}?unzoned=0`)).json(),
  });
  const hasZones = (data?.zones.length ?? 0) > 0 || account.deliveryZoneId != null;
  // Under the account's key: an address, pick or zone change on this page or the zones
  // card invalidates it. Read afresh each time the page opens, because the delivery
  // schedule (saved in Settings, under another key) changes it too.
  const { data: days } = useQuery<AccountDeliveryDays>({
    queryKey: ["/api/accounts", account.id, "delivery-days"],
    queryFn: async () => (await apiRequest("GET", `/api/accounts/${account.id}/delivery-days`)).json(),
    enabled: hasZones && !account.isGroupParent,
    staleTime: 0,
  });

  const assign = useMutation({
    mutationFn: (value: string) => apiRequest("PUT", `/api/accounts/${account.id}/delivery-zone`, zonePickBody(value)),
    onSuccess: invalidate,
    onError: (error) =>
      toast({ title: "Couldn't set the delivery zone", description: parseApiError(error).message ?? "Please try again.", variant: "destructive" }),
  });

  // No zones in this org: nothing to choose — the page stays as it was.
  if (!hasZones) return null;

  const manual = account.deliveryZoneSource === "manual";
  const current = manual ? (account.deliveryZoneId == null ? ZONE_PICK_NONE : String(account.deliveryZoneId)) : ZONE_PICK_MATCH;
  const zones = zonesByFirstDay(data?.zones ?? [], days?.orgDays ?? 0);
  const place = days ? [days.town, days.postalArea].filter(Boolean).join(", ") : "";
  // The account's zones, in the order the zones are shown everywhere else.
  const shown = zones.map((z) => z.id);
  const mine = [...(days?.zones ?? [])].sort((a, b) => shown.indexOf(a.id) - shown.indexOf(b.id));

  return (
    <Card data-testid="card-account-delivery-zone">
      <CardHeader className="border-b border-gray-100 bg-gray-50/50">
        <CardTitle className="flex items-center gap-2">
          <MapPinned className="h-5 w-5" />
          Delivery Days
        </CardTitle>
        <CardDescription className="mt-1">
          The delivery days this customer can choose, and the zones they come from.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {account.isGroupParent ? (
          <p className="text-sm text-gray-600">
            Orders are placed by this group's locations, so each location has its own delivery days. See each
            location's page.
          </p>
        ) : (
        <>
        {days && (
          <div className="space-y-1" data-testid="text-account-zone-effect">
            <p className="font-medium" data-testid="text-account-delivery-days">{daysLabel(days.deliveryDays)}</p>
            {!days.zonesApplied ? (
              <p className="text-sm text-amber-700">
                Zones aren't applied yet, so this customer gets all your delivery days.
                {days.zones.length > 0 && ` Once they are: ${daysLabel(days.zoneDays)}.`}
              </p>
            ) : days.zones.length === 0 ? (
              <p className="text-sm text-gray-600">
                {days.source === "manual"
                  ? "Left without a zone by hand, so this customer gets all your delivery days."
                  : `No zone lists this address${place ? ` (${place})` : ""}, so this customer gets all your delivery days.`}
              </p>
            ) : null}
            {mine.length > 0 && (
              <div className="text-sm text-gray-600">
                <p>
                  {days.source === "manual"
                    ? "Set by hand to one zone:"
                    : `From ${days.zones.length === 1 ? "the zone that lists" : `the ${days.zones.length} zones that list`} this address${place ? ` (${place})` : ""}:`}
                </p>
                <ul className="mt-1 space-y-0.5" data-testid="list-account-zones">
                  {mine.map((zone) => (
                    <li key={zone.id} className="flex justify-between gap-3">
                      <span className="min-w-0 break-words">{zone.name}</span>
                      <span className="shrink-0 text-gray-500">{daysLabel(zone.deliveryDays)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Label htmlFor="account-delivery-zone">Zones</Label>
          <div className="flex items-center gap-2">
            {assign.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <Select
              value={current}
              disabled={assign.isPending}
              onValueChange={(value) => {
                if (value !== current) assign.mutate(value);
              }}
            >
              <SelectTrigger id="account-delivery-zone" className="w-full sm:w-64" data-testid="select-account-delivery-zone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ZONE_PICK_MATCH}>Match by address</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={String(z.id)}>
                    Only {z.name}
                  </SelectItem>
                ))}
                <SelectItem value={ZONE_PICK_NONE}>No zone</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  );
}
